import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import gmapInit from 'app/client/utils/map/gmapInit';
import { POIMarkers, type POIType } from '../POIMarkers';
import type { CommuteDestination, TravelMode } from '../utils/commuteTypes';
import { createCachedListingMarkerIcon, getListingIconColor, getListingIconType } from '../utils/listingIcons';
import { createCommuteDestinationMarkerIcon } from '../utils/mapMarkers';
import { isValidCoordinate } from '../utils/coordinates';
import {
  dynamicMapContainer,
  fallbackContainer,
  fallbackIcon,
  fallbackSubtext,
  fallbackText,
  loadingContainer,
  mapElement,
} from './dynamicMap.styles';

import type { RootReduxState } from 'app/shared/flux/reducers/index';

export type DynamicMapProps = {
  lat: number;
  lon: number;
  address: string;
  width?: number;
  height?: number;
  zoom?: number;
  className?: string;
  // Listing Data for Icon System (gmapIcons.ts compatible)
  listing?: {
    id?: string;
    iconType?: string;
    listingType?: string;
    propertyType?: string;
    unitType?: string;
    buildingType?: string;
    userItemTypes?: Array<string>;
    isFavorited?: boolean;
    isActive?: boolean;
  };
  viewedListingIds?: Set<string>;
  // POI Integration
  activePOITypes?: Set<POIType>;
  onPOILoaded?: (results: Array<any>, poiType: POIType) => void;
  onPOIError?: (error: Error, poiType: POIType) => void;
  // Map Instance Callback
  onMapLoad?: (map: google.maps.Map) => void;
  // Dynamic Bounds Control
  allowExpandedBounds?: boolean;
  // Commute Route Visualization
  commuteDestination?: CommuteDestination | null;
  selectedCommuteMode?: TravelMode;
};

type MapState = 'loading' | 'loaded' | 'error';

// Constant empty set to prevent unnecessary POIMarkers re-renders
const EMPTY_POI_SET = new Set<POIType>();
const MAP_READY_WATCHDOG_MS = 10000;

// Google Maps v3.x lazily defers tile rendering for a container that's outside
// the viewport at construction time — it never fires tilesloaded/idle until the
// element is scrolled (nearly) into view. Constructing the map while off-screen
// therefore trips the readiness watchdog above and strands the module on the
// "Map unavailable" fallback. We mirror that laziness with an IntersectionObserver
// and start a little early so the map is ready by the time it's visible.
const MAP_VIEWPORT_PRELOAD_MARGIN = '200px';

// HALO map styles - same as StaticMap for visual consistency
const HALO_MAP_STYLES = [
  {
    featureType: 'water',
    elementType: 'all',
    stylers: [{ color: '#9dced1' }, { visibility: 'on' }],
  },
  {
    featureType: 'landscape',
    elementType: 'all',
    stylers: [{ visibility: 'simplified' }],
  },
  {
    featureType: 'landscape.man_made',
    elementType: 'all',
    stylers: [{ visibility: 'on' }, { hue: '#f0e0c8' }, { gamma: 1.75 }],
  },
  {
    featureType: 'poi',
    elementType: 'geometry',
    stylers: [{ color: '#edebdf' }, { visibility: 'on' }],
  },
  {
    featureType: 'poi.park',
    elementType: 'geometry',
    stylers: [{ color: '#d8ebd2' }],
  },
  {
    featureType: 'poi.park',
    elementType: 'labels',
    stylers: [{ visibility: 'on' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry.fill',
    stylers: [{ color: '#ffe7c5' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#fadbaf' }],
  },
  {
    featureType: 'road',
    elementType: 'geometry.stroke',
    stylers: [{ weight: 1 }, { visibility: 'on' }],
  },
  {
    featureType: 'transit',
    elementType: 'all',
    stylers: [{ visibility: 'on' }],
  },
  {
    featureType: 'administrative',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#888888' }],
  },
];

/**
 * DynamicMap - Interactive Google Maps component for HALO NeighborhoodModule
 *
 * Replaces StaticMap with a lightweight, interactive map that provides:
 * - Pan and zoom navigation around the listing location
 * - Modern SVG-based listing icons (building/house/room) with color states
 * - Clickable listing marker with info popup
 * - Zoom-responsive icon sizing for optimal visibility
 * - Same HALO styling and visual appearance as StaticMap
 * - Maintains all error handling and accessibility patterns
 */
export const DynamicMap = ({
  lat,
  lon,
  address,
  width = 400,
  height = 200,
  zoom = 14,
  className,
  listing,
  viewedListingIds = new Set(),
  activePOITypes,
  onPOILoaded,
  onPOIError,
  onMapLoad,
  allowExpandedBounds = false,
  commuteDestination,
  selectedCommuteMode,
}: DynamicMapProps) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.Marker | null>(null);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);

  // Commute route visualization refs
  const commutePolylineRef = useRef<google.maps.Polyline | null>(null);
  const commuteDestinationMarkerRef = useRef<google.maps.Marker | null>(null);

  const [mapState, setMapState] = useState<MapState>('loading');
  const [containerVisible, setContainerVisible] = useState(true);
  // Incremented on hidden→visible transitions to force map recreation
  const [mapRevision, setMapRevision] = useState(0);
  // Gates map creation on viewport visibility (see MAP_VIEWPORT_PRELOAD_MARGIN).
  const [inViewport, setInViewport] = useState(false);

  // Get Google Maps API status from Redux
  const gmapApiReady = useSelector((state: RootReduxState) => state.app.gmapApiReady);
  const googleMapsKey = useSelector((state: RootReduxState) => state.app.googleMaps?.key);

  // Coordinate validation is now handled by the pure function above

  // Initialize Google Maps API if not loaded
  useEffect(() => {
    if (!gmapApiReady && googleMapsKey) {
      setMapState('loading');
      gmapInit({ key: googleMapsKey })
        .then(() => {
          // Success! The Redux action will update gmapApiReady
          // Component will re-render and second useEffect will run
        })
        .catch((error: Error) => {
          console.warn('DynamicMap: Failed to initialize Google Maps API:', error.message);
          setMapState('error');
        });
    }
  }, [gmapApiReady, googleMapsKey]);

  // Create map instance and marker when API is ready.
  // Re-runs when mapRevision bumps OR when containerVisible flips, so a map born
  // into a 0×0 container (e.g. behind a Suspense/loadable boundary that hasn't
  // resolved yet, or inside an animating panel) is recreated as soon as the
  // container has real dimensions. Google Maps refuses to fetch tiles into a
  // zero-sized canvas, so without the visibility gate the very first map sits
  // mounted-but-blank, never fires tilesloaded, and the watchdog flips to error.
  useEffect(() => {
    if (!gmapApiReady || !mapRef.current) {
      return;
    }

    // Don't construct the map until it's on (or near) screen. Google won't
    // render tiles into an off-screen container, so building it now would only
    // time out the readiness watchdog and flip us to the error fallback. The
    // IntersectionObserver effect flips inViewport and re-runs this effect.
    if (!inViewport) {
      return;
    }

    // Don't try to build a map until the container has real dimensions —
    // Google Maps won't request tiles into a 0×0 canvas, so we'd just sit
    // here until the watchdog times out. The containerVisible dep below
    // re-runs this effect when dimensions finally arrive.
    if (mapRef.current.clientWidth === 0 || mapRef.current.clientHeight === 0) {
      setContainerVisible(false);
      return;
    }

    // Validate coordinates
    if (!isValidCoordinate(lat, lon)) {
      console.warn('DynamicMap: Invalid coordinates provided', { lat, lon });
      setMapState('error');
      return;
    }

    // Clean up previous map instance (needed when mapRevision bumps)
    if (mapInstanceRef.current) {
      if (infoWindowRef.current) infoWindowRef.current.close();
      if (markerRef.current) markerRef.current.setMap(null);
      if (commutePolylineRef.current) commutePolylineRef.current.setMap(null);
      if (commuteDestinationMarkerRef.current) commuteDestinationMarkerRef.current.setMap(null);
      window.google?.maps?.event?.clearInstanceListeners(mapInstanceRef.current);
      mapInstanceRef.current = null;
      markerRef.current = null;
      commutePolylineRef.current = null;
      commuteDestinationMarkerRef.current = null;
    }

    try {
      setMapState('loading');

      // Create map instance
      const mapOptions: google.maps.MapOptions = {
        center: { lat, lng: lon },
        zoom,
        styles: HALO_MAP_STYLES,
        // Disable all controls for pure map display
        streetViewControl: false,
        mapTypeControl: false,
        fullscreenControl: false,
        zoomControl: false,
        gestureHandling: 'cooperative',
        // Constrain the viewport to prevent excessive panning (unless expanded bounds are allowed)
        restriction: allowExpandedBounds
          ? null
          : {
              latLngBounds: {
                north: lat + 0.01,
                south: lat - 0.01,
                east: lon + 0.01,
                west: lon - 0.01,
              },
            },
      };

      const map = new google.maps.Map(mapRef.current, mapOptions);
      mapInstanceRef.current = map;

      // Notify parent component about map instance
      if (onMapLoad) {
        onMapLoad(map);
      }

      // Create marker at listing location using modern SVG icon system
      const iconType = listing ? getListingIconType(listing) : 'building';

      // Provide fallback ID for backward compatibility when listing.id is missing
      const listingWithId = listing
        ? {
            ...listing,
            id: listing.id || `${lat}-${lon}`, // Use coordinates as fallback ID
          }
        : { id: `${lat}-${lon}` };

      const iconColor = getListingIconColor(listingWithId, viewedListingIds);
      // Increase listing marker size to be more prominent than 24px POI markers
      const markerSize = Math.max(zoom * 2, 28); // Responsive size (28-40px range) - larger than POI markers

      const marker = new google.maps.Marker({
        position: { lat, lng: lon },
        map,
        icon: createCachedListingMarkerIcon(iconType, iconColor, markerSize),
        title: address,
        // Make marker clickable
        clickable: true,
      });
      markerRef.current = marker;

      // Create info window for marker
      const infoWindow = new google.maps.InfoWindow({
        content: `
          <div style="padding: 8px; max-width: 200px;">
            <div style="font-weight: 600; color: #2f3c53; margin-bottom: 4px;">
              Property Location
            </div>
            <div style="font-size: 14px; color: #6e6e6e;">
              ${address}
            </div>
          </div>
        `,
      });
      infoWindowRef.current = infoWindow;

      // Add click handler to marker
      if (typeof marker.addListener === 'function') {
        marker.addListener('click', () => {
          infoWindow.open(map, marker);
        });
      }

      let mapReady = false;
      let mapReadyWatchdog: ReturnType<typeof setTimeout> | null = null;
      let idleListener: google.maps.MapsEventListener | null = null;
      let tilesLoadedListener: google.maps.MapsEventListener | null = null;

      const clearMapReadyFallbacks = () => {
        if (idleListener) {
          google.maps.event.removeListener(idleListener);
        }
        if (tilesLoadedListener) {
          google.maps.event.removeListener(tilesLoadedListener);
        }
        if (mapReadyWatchdog) {
          clearTimeout(mapReadyWatchdog);
        }
      };

      const handleMapReady = () => {
        if (mapReady) {
          return;
        }

        mapReady = true;
        setMapState('loaded');
        clearMapReadyFallbacks();
        // Show the address bubble on first paint so users see the listing
        // location without having to click the marker.
        infoWindow.open({ map, anchor: marker, shouldFocus: false });
      };

      // Whichever fires first (tilesloaded is usually faster than idle) handles
      // both state and InfoWindow; handleMapReady removes both listeners.
      idleListener = map.addListener('idle', handleMapReady);
      tilesLoadedListener = map.addListener('tilesloaded', handleMapReady);

      mapReadyWatchdog = setTimeout(() => {
        if (mapReady) {
          return;
        }

        mapReady = true;
        console.warn('DynamicMap: Map readiness timed out');
        clearMapReadyFallbacks();
        setMapState('error');
      }, MAP_READY_WATCHDOG_MS);

      return () => {
        mapReady = true;
        clearMapReadyFallbacks();
      };
    } catch (error) {
      console.warn('DynamicMap: Error creating map:', error);
      setMapState('error');
    }
  }, [gmapApiReady, mapRevision, containerVisible, inViewport]);

  // Update map options when bounds permission changes (without recreating map)
  useEffect(() => {
    if (!mapInstanceRef.current) return;

    mapInstanceRef.current.setOptions({
      restriction: allowExpandedBounds
        ? null
        : {
            latLngBounds: {
              north: lat + 0.01,
              south: lat - 0.01,
              east: lon + 0.01,
              west: lon - 0.01,
            },
          },
    });
  }, [allowExpandedBounds, lat, lon]);

  // Commute route visualization.
  // Gated on containerVisible so we never call fitBounds on a zero-size container
  // (mobile FullscreenOverlay hides body children). When the overlay closes and
  // containerVisible flips to true, this effect re-runs and draws the route fresh
  // so tiles load at the correct zoom level.
  useEffect(() => {
    // Skip everything while container is hidden — don't draw or clear
    if (!containerVisible) return;

    if (!mapInstanceRef.current || !commuteDestination || !selectedCommuteMode) {
      // Clear existing route if no commute data
      if (commutePolylineRef.current) {
        commutePolylineRef.current.setMap(null);
        commutePolylineRef.current = null;
      }
      if (commuteDestinationMarkerRef.current) {
        commuteDestinationMarkerRef.current.setMap(null);
        commuteDestinationMarkerRef.current = null;
      }
      return;
    }

    const map = mapInstanceRef.current;

    // Use Google Directions Service to get route
    const directionsService = new google.maps.DirectionsService();
    const origin = new google.maps.LatLng(lat, lon);
    const destination = new google.maps.LatLng(commuteDestination.lat, commuteDestination.lng);

    // Map travel modes to Google Directions travel modes
    const getTravelMode = (mode: TravelMode): google.maps.TravelMode => {
      switch (mode) {
        case 'driving':
          return google.maps.TravelMode.DRIVING;
        case 'walking':
          return google.maps.TravelMode.WALKING;
        case 'transit':
          return google.maps.TravelMode.TRANSIT;
        case 'bicycling':
          return google.maps.TravelMode.BICYCLING;
        default:
          return google.maps.TravelMode.DRIVING;
      }
    };

    directionsService.route(
      {
        origin,
        destination,
        travelMode: getTravelMode(selectedCommuteMode),
      },
      (result, status) => {
        if (status === google.maps.DirectionsStatus.OK && result) {
          // Clear existing polyline
          if (commutePolylineRef.current) {
            commutePolylineRef.current.setMap(null);
          }

          // Create route polyline
          const routePath = result.routes[0].overview_path;
          commutePolylineRef.current = new google.maps.Polyline({
            path: routePath,
            geodesic: true,
            strokeColor: '#007a70', // hpxTeal500
            strokeOpacity: 0.8,
            strokeWeight: 4,
            map,
          });

          // Clear existing destination marker
          if (commuteDestinationMarkerRef.current) {
            commuteDestinationMarkerRef.current.setMap(null);
          }

          // Create destination marker
          commuteDestinationMarkerRef.current = new google.maps.Marker({
            position: destination,
            map,
            title: commuteDestination.name,
            icon: createCommuteDestinationMarkerIcon(),
          });

          // Adjust map bounds to show both listing and commute destination
          const bounds = new google.maps.LatLngBounds();
          bounds.extend(origin);
          bounds.extend(destination);

          map.fitBounds(bounds, {
            top: 50,
            right: 50,
            bottom: 50,
            left: 50,
          });
        }
      },
    );
  }, [commuteDestination, selectedCommuteMode, lat, lon, containerVisible]);

  // Track container visibility. On mobile, FullscreenOverlay hides body children
  // (display:none) while the spoke is open, giving the map container zero dimensions.
  // When the container transitions from hidden to visible, we bump mapRevision to
  // force full map recreation (Google Maps tiles corrupt after display:none).
  //
  // Empty dep array: keep this observer attached for the lifetime of the component
  // so wasHidden survives mapState transitions. Previously the dep was [mapState],
  // which tore the observer down and reset wasHidden whenever the watchdog flipped
  // state to 'error' — leaving the component permanently stuck on the error UI
  // even after the container became visible.
  useEffect(() => {
    const el = mapRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;

    let wasHidden = false;

    const observer = new ResizeObserver(() => {
      const visible = el.clientWidth > 0 && el.clientHeight > 0;
      if (wasHidden && visible) {
        setMapRevision((r) => r + 1);
      }
      wasHidden = !visible;
      setContainerVisible(visible);
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Defer map creation until the container is (nearly) in the viewport. Google
  // Maps lazily renders off-screen maps and never fires tilesloaded/idle until
  // they're scrolled into view; without this gate a neighborhood map that sits
  // below the fold builds immediately, never paints, and the readiness watchdog
  // flips it to "Map unavailable" before the user ever scrolls down to it.
  useEffect(() => {
    const el = mapRef.current;
    if (!el) return;

    // No IntersectionObserver (SSR / older runtimes): create eagerly.
    if (typeof IntersectionObserver === 'undefined') {
      setInViewport(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setInViewport(true);
          observer.disconnect();
        }
      },
      { rootMargin: MAP_VIEWPORT_PRELOAD_MARGIN },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (infoWindowRef.current && typeof infoWindowRef.current.close === 'function') {
        infoWindowRef.current.close();
      }
      if (markerRef.current && typeof markerRef.current.setMap === 'function') {
        markerRef.current.setMap(null);
      }
      if (commutePolylineRef.current && typeof commutePolylineRef.current.setMap === 'function') {
        commutePolylineRef.current.setMap(null);
      }
      if (commuteDestinationMarkerRef.current && typeof commuteDestinationMarkerRef.current.setMap === 'function') {
        commuteDestinationMarkerRef.current.setMap(null);
      }
      if (mapInstanceRef.current && window.google?.maps?.event?.clearInstanceListeners) {
        window.google.maps.event.clearInstanceListeners(mapInstanceRef.current);
      }
    };
  }, []);

  const LoadingContent = () => <div className={loadingContainer} data-testid="dynamic-map-loading" />;

  const FallbackContent = () => (
    <div className={fallbackContainer} data-testid="dynamic-map-fallback">
      <div className={fallbackIcon}>🗺️</div>
      <div className={fallbackText}>Map unavailable</div>
      <div className={fallbackSubtext}>{address ? `Location: ${address}` : 'Unable to load map'}</div>
    </div>
  );

  return (
    <div className={`${dynamicMapContainer} ${className || ''}`}>
      {mapState === 'loading' && <LoadingContent />}

      {mapState === 'error' && <FallbackContent />}

      {/* Map container - always present for API to attach to */}
      <div
        ref={mapRef}
        className={mapElement}
        style={{
          width: '100%',
          height: '100%',
          opacity: mapState === 'loaded' ? 1 : 0,
          animation: mapState === 'loaded' ? 'fadingIn 0.25s ease-out' : 'none',
        }}
        data-testid="dynamic-map-element"
        role="img"
        aria-label={`Interactive map showing ${address}`}
      />

      {/* POI Markers - render when map is loaded, handles empty POI types internally */}
      {mapState === 'loaded' && mapInstanceRef.current && (
        <POIMarkers
          key="poi-markers" // Stable key to prevent unnecessary remounting
          map={mapInstanceRef.current}
          centerLat={lat}
          centerLng={lon}
          activePOITypes={activePOITypes || EMPTY_POI_SET}
          onPOILoaded={onPOILoaded}
          onPOIError={onPOIError}
        />
      )}
    </div>
  );
};
